<script setup lang="ts">
import {
    AppWindowIcon,
    BookMarkedIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    CreditCardIcon,
    GlobeIcon,
    LinkIcon,
    LogOutIcon,
    MonitorSmartphoneIcon,
    MoonIcon,
    PencilIcon,
    PlayIcon,
    PinIcon,
    PinOffIcon,
    SunMediumIcon,
    WrenchIcon,
    RotateCcwIcon,
    EyeOffIcon,
    WifiSyncIcon,
    PlusIcon,
} from 'lucide-vue-next';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { eventEmitter } from '@/lib/eventEmitter.ts';
import { toggleDarkModeAction } from '@/actions/app/ToggleDarkModeAction.ts';
import { router, ROUTES } from '@/router.ts';
import InputSourceSelection from '@/components/menu/InputSourceSelection.vue';
import { startSessionAction } from '@/actions/run/StartSessionAction.ts';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { computed } from 'vue';
import { openWebViewAction } from '@/actions/app/OpenWebViewAction.ts';
import { SubscriptionTier } from '@/types/SubscriptionTier.ts';
import { startSubscriptionProcessAction } from '@/actions/app/StartSubscriptionProcessAction.ts';
import { useUserInfoStore } from '@/stores/UserInfoStore.ts';
import { logOutAction } from '@/actions/auth/LogOutAction.ts';
import HotkeyIndicator from '@/components/hotkey/HotkeyIndicator.vue';
import { Hotkey } from '@/types/Hotkey.ts';
import { resetAction } from '@/actions/app/ResetAction.ts';
import { hideAppAction } from '@/actions/electron/HideAppAction.ts';
import { openWebsiteAction } from '@/actions/app/OpenWebsiteAction.ts';
import { openSubscriptionManagementAction } from '@/actions/app/OpenSubscriptionManagementAction.ts';
import { DefaultProfileConfigs } from '@/constants/DefaultProfileConfigs.ts';
import { ProfileConfigService } from '@/services/config/ProfileConfigService.ts';
import { setAppAlwaysOnTopAction } from '@/actions/electron/SetAppAlwaysOnTopAction.ts';
import { syncStartDesktopAppSessionAction } from '@/actions/sync/SyncStartDesktopAppSessionAction.ts';
import { useWebSocketStore } from '@/stores/WebSocketStore.ts';
import { useClientStore } from '@/stores/ClientStore.ts';
import { useRoute } from 'vue-router';
import { MAX_SCREEN_CAPTURES } from '@/actions/run/AttachScreenshotAction.ts';

const settingsStore = useSettingsStore();
const sessionStore = useSessionStore();
const userInfoStore = useUserInfoStore();
const webSocketStore = useWebSocketStore();
const clientStore = useClientStore();
const route = useRoute();

/* region Event handlers */
const startSession = async () => {
    await startSessionAction(true);
};

const startDesktopAppSession = async () => {
    await syncStartDesktopAppSessionAction(true);
};

const setActiveProfileId = async (profileId: ProfileConfig['id'] | 'new') => {
    if (profileId === 'edit') {
        await router.push(ROUTES.editIndex);
        return;
    }

    settingsStore.selectedProfileId = profileId;
};

const setAlwaysOnTop = async () => {
    await setAppAlwaysOnTopAction(!settingsStore.appAlwaysOnTop);
};

const showSettingsDialog = () => {
    eventEmitter.emit('setSettingsDialogVisibility', { visible: true });
};
/* endregion Event handlers */

/* region Computed */
const selectedProfileId = computed((): ProfileConfig['id'] | undefined => {
    return settingsStore.selectedProfileId;
});
const isActiveRun = computed(
    () =>
        route.name === ROUTES.runIndex &&
        route.params.sessionId === clientStore.activeSessionId &&
        route.params.profileConfigId === settingsStore.selectedProfileId,
);
const screenCaptureCount = computed(() => {
    const sessionId = clientStore.activeSessionId;
    return sessionId ? (clientStore.screenCapturesBySession[sessionId]?.length ?? 0) : 0;
});
const screenCaptureLimitReached = computed(() => screenCaptureCount.value >= MAX_SCREEN_CAPTURES);
/* endregion Computed */
</script>

<template>
    <div class="flex w-screen justify-between border-b dark:bg-zinc-900/80">
        <!-- Left -->
        <div class="flex">
            <!-- Action button -->
            <div class="flex items-center">
                <Select :model-value="selectedProfileId" @update:model-value="setActiveProfileId">
                    <SelectTrigger
                        :disabled="sessionStore.isInsideWebView || settingsStore.isOnboarding"
                        class="h-[38px] rounded-none focus:ring-0 focus:ring-offset-0"
                    >
                        <SelectValue
                            placeholder="Select a profile&nbsp;"
                            :class="{ 'text-red-600': !selectedProfileId }"
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <!-- Default profiles -->
                        <SelectGroup>
                            <SelectLabel>Default profiles</SelectLabel>
                            <SelectItem
                                v-for="profile of Object.values(DefaultProfileConfigs).sort(
                                    (a, b) => a.name.localeCompare(b.name),
                                )"
                                :value="profile.id"
                                :key="profile.id"
                            >
                                {{ profile.name }}&nbsp;
                            </SelectItem>
                        </SelectGroup>

                        <!-- Custom profiles -->
                        <SelectGroup
                            v-if="
                                Object.values(ProfileConfigService.getCustomProfileConfigs())
                                    .length > 0
                            "
                        >
                            <SelectLabel>Custom profiles</SelectLabel>
                            <SelectItem
                                v-for="profile of Object.values(
                                    ProfileConfigService.getCustomProfileConfigs(),
                                ).sort((a, b) => a.name.localeCompare(b.name))"
                                :value="profile.id"
                                :key="profile.id"
                            >
                                {{ profile.name }}&nbsp;
                            </SelectItem>
                        </SelectGroup>

                        <!-- Edit mode button -->
                        <template
                            v-if="!sessionStore.isInsideWebView && !settingsStore.isOnboarding"
                        >
                            <SelectSeparator />
                            <SelectItem value="edit" :hide-indicator="true">
                                <div class="flex items-center justify-center">
                                    <PencilIcon class="me-2 size-3.5" />
                                    Edit prompts&nbsp;
                                </div>
                            </SelectItem>
                        </template>
                    </SelectContent>
                </Select>
                <Button
                    v-if="!sessionStore.isInsideWebView"
                    class="rounded-none"
                    variant="success"
                    @click="startSession"
                    :disabled="
                        !settingsStore.selectedProfileId ||
                        settingsStore.isOnboarding ||
                        (isActiveRun && screenCaptureLimitReached)
                    "
                >
                    <PlusIcon v-if="isActiveRun" />
                    <PlayIcon v-else />
                    <template v-if="isActiveRun">
                        Add screenshot ({{ screenCaptureCount }}/{{ MAX_SCREEN_CAPTURES }})
                    </template>
                    <template v-else>Run</template>
                    <HotkeyIndicator :hotkey="Hotkey.ActionX1" class="border-gray-300" />
                </Button>
                <Button
                    v-else
                    class="rounded-none"
                    variant="success"
                    @click="startDesktopAppSession"
                    :disabled="
                        !settingsStore.selectedProfileId ||
                        settingsStore.isOnboarding ||
                        sessionStore.isRunBlocked ||
                        !webSocketStore.isConnected ||
                        !webSocketStore.connectedClients.find((client) => client.isDesktopApp)
                    "
                >
                    <WifiSyncIcon />
                    Run in desktop app
                </Button>
            </div>

            <!-- Settings button -->
            <div class="flex items-center">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button class="rounded-none hover:no-underline" variant="link">
                            <WrenchIcon />
                            <span class="hidden md:inline">Settings</span>
                            <ChevronDownIcon />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                        <InputSourceSelection v-if="!sessionStore.isInsideWebView" />
                        <DropdownMenuItem @click="toggleDarkModeAction()">
                            <template v-if="settingsStore.darkModeEnabled">
                                <SunMediumIcon />
                                Light mode
                            </template>
                            <template v-else>
                                <MoonIcon />
                                Dark mode
                            </template>
                        </DropdownMenuItem>
                        <template v-if="!sessionStore.isInsideWebView">
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                @click="resetAction(true)"
                                :disabled="settingsStore.isOnboarding"
                            >
                                <RotateCcwIcon />
                                Reset
                                <HotkeyIndicator
                                    :hotkey="Hotkey.ActionX2"
                                    class="border-gray-300"
                                />
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                @click="hideAppAction"
                                :disabled="settingsStore.isOnboarding"
                            >
                                <EyeOffIcon />
                                Hide app
                                <HotkeyIndicator
                                    :hotkey="Hotkey.ActionX3"
                                    class="border-gray-300"
                                />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                @click="setAlwaysOnTop"
                                :disabled="settingsStore.isOnboarding"
                            >
                                <template v-if="settingsStore.appAlwaysOnTop">
                                    <PinOffIcon />
                                    Disable app on top
                                </template>
                                <template v-else>
                                    <PinIcon />
                                    Pin app on top
                                </template>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                @click="showSettingsDialog"
                                :disabled="settingsStore.isOnboarding"
                            >
                                <AppWindowIcon />
                                All settings
                            </DropdownMenuItem>
                        </template>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>

        <!-- Right -->
        <div class="flex">
            <!-- Subscribe button -->
            <Button
                v-if="
                    userInfoStore.subscriptionTier !== SubscriptionTier.Pro &&
                    !settingsStore.isOnboarding
                "
                variant="success"
                class="rounded-none"
                @click="startSubscriptionProcessAction"
            >
                Subscribe
            </Button>
            <Button
                v-if="!sessionStore.isInsideWebView && !settingsStore.isOnboarding"
                variant="outline"
                class="rounded-none border-none"
                @click="openWebViewAction"
            >
                <MonitorSmartphoneIcon />
                <span class="hidden sm:inline">Web view</span>
            </Button>
            <!-- Outgoing button -->
            <div class="flex items-center">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button class="rounded-none" variant="secondary">
                            <LinkIcon />
                            <span class="hidden md:inline">Links</span>
                            <ChevronRightIcon />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                        <DropdownMenuItem
                            @click="openWebsiteAction('https://leetcodewizard.io/help')"
                        >
                            <BookMarkedIcon />
                            Help center
                        </DropdownMenuItem>
                        <DropdownMenuItem @click="openWebsiteAction('https://leetcodewizard.io/')">
                            <GlobeIcon />Website</DropdownMenuItem
                        >
                        <DropdownMenuSeparator />
                        <template v-if="!sessionStore.isInsideWebView">
                            <DropdownMenuItem
                                @click="openSubscriptionManagementAction"
                                :disabled="settingsStore.isOnboarding"
                                ><CreditCardIcon />Billing</DropdownMenuItem
                            >
                            <DropdownMenuSeparator />
                        </template>
                        <DropdownMenuItem @click="logOutAction"
                            ><LogOutIcon />Log out</DropdownMenuItem
                        >
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    </div>
</template>
